import { useMemo } from 'react';
import { Highlighter } from 'react-bootstrap-typeahead';

import { searchEvents } from 'api/events/endpoints/searchEvents';
import type { EventRow } from 'api/events/endpoints/getEvents';

import { EditReference } from './EditReference';
import type { ReferenceSource } from './referenceSource';

interface Props {
  // The stored event_id, or null for "no event".
  readonly value: number | null;
  readonly onChange: (value: number | null) => void;
  /** Floating label. Omit to render a bare control (the caller supplies its own label), as `EditString` does. */
  readonly label?: string;
  /** Text shown while nothing is selected. Say what empty means, rather than restating the label. */
  readonly placeholder?: string;
  readonly mandatory?: boolean;
  readonly disabled?: boolean;
  // Restrict the searchable set to one zone (server-side `zone_name` equals). Undefined widens to
  // all authorized events.
  readonly zoneName?: string;
  // Which event kinds are selectable. `'all'` widens to every kind.
  readonly type?: 'processEvent' | 'fileEvent' | 'all';
}

const getKey = (e: EventRow) => e.event_id;
const getLabel = (e: EventRow): string => e.name;

/**
 * Searchable dropdown for events keyed on `event_id`, for callers that store the numeric id. The
 * id↔display-name expansion is internal: the caller works in plain ids, and `resolve` hydrates a
 * stored id to its name. Selection returns the id.
 *
 * Sibling of the path-keyed {@link EditEventPathReference}, whose props are kept in step with these:
 * only `value` / `onChange` differ, in type *and* in meaning, which is the whole reason there are two
 * components rather than one with a `string | number` value.
 *
 * Backed by server-side search, so the option list is not bounded by what fits in one eager fetch.
 * Two-column row layout (name + zone, muted right-aligned as informational context); the server
 * matches on name only, zone is shown so users can disambiguate visually.
 */
export const EditEventReference = ({ value, onChange, label, placeholder, mandatory, disabled, zoneName, type = 'processEvent' }: Props) => {
  // Inflate the stored id to the key-only sentinel EditReference selects on; the empty name defers
  // label resolution to `resolve`. Memoised on the id so the reference model isn't rebuilt each render.
  const selected = useMemo<EventRow | null>(() => (value !== null ? { event_id: value, name: '', zone_name: '' } : null), [value]);

  // Re-created when the scope changes so the model re-fetches; stable otherwise (a fresh closure
  // would rebuild the model every render).
  const source = useMemo<ReferenceSource<EventRow>>(() => {
    const kind = type === 'all' ? undefined : type;
    return {
      kind: 'search',
      // `name` rather than `search`: the backend's `search` also matches on description, which would
      // show rows whose visible label does not contain what the user typed.
      search: (query, limit) => searchEvents({ name: query || undefined, limit, zone_name: zoneName, type: kind }),
      // Resolve a saved id back to its label by id alone — independent of the zone/type scope, so an
      // existing selection still renders even once the scope no longer admits it.
      resolve: async (key) => {
        const { items } = await searchEvents({ event_id: Number(key), limit: 1 });
        return items[0] ?? null;
      },
      // Shown while `resolve` is in flight so a key-only value reads as selected rather than blank.
      interimValue: (key) => ({ event_id: Number(key), name: `Event #${key}`, zone_name: '' }),
    };
  }, [zoneName, type]);

  return (
    <EditReference<EventRow>
      label={label}
      placeholder={placeholder}
      value={selected}
      onChange={(e) => onChange(e?.event_id ?? null)}
      source={source}
      getKey={getKey}
      getLabel={getLabel}
      mandatory={mandatory}
      disabled={disabled}
      renderMenuItemChildren={(e, query) => (
        <div className="d-flex justify-content-between gap-3">
          <span>
            <Highlighter search={query}>{e.name}</Highlighter>
          </span>
          <span className="text-body-secondary">{e.zone_name}</span>
        </div>
      )}
    />
  );
};
